import { defineStore } from "pinia";
import {
  ProjectsApi,
  ProjectsImportApi,
  TrainingPlanApi,
  type TrainingPanFindOneDetails,
  type RemoveProjectUsersDtoRequestActionEnum,
  RemoveBookingDtoRequestActionEnum,
  type ProjectImportResultResponse,
  type ProjectSearchItem,
  type ProjectSearchPaginationMeta,
} from "@simbelapp/order-sdk";
import { useSessionsStore } from "../sessions/sessions-store";
import type { IProjectListItem } from "~/utils/interfaces/project-interfaces";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useFeedback } from "~/composables/feedback/useFeedback";

interface IProjectStore {
  trainingPlanProjectIndividual: TrainingPanFindOneDetails | null;
  showCsvImportModal: boolean;
  projectsList: IProjectListItem[];
}

export const useProjectsStore = defineStore("projectsStore", {
  state: (): IProjectStore => {
    return {
      trainingPlanProjectIndividual: null,
      showCsvImportModal: false,
      projectsList: [],
    };
  },
  getters: {},
  actions: {
    /** Server-searched option source for the draft-session dropdown. */
    async searchProjects(
      params: Record<string, any> = {},
    ): Promise<{ data: ProjectSearchItem[]; meta: ProjectSearchPaginationMeta | null }> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);
        const response = await apiInstance.searchProjects(params);
        return { data: response.data ?? [], meta: response.meta };
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
        return { data: [], meta: null };
      }
    },

    async uploadProjectsCsv(file: Blob): Promise<ProjectImportResultResponse | null> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsImportApi);
        return await apiInstance.uploadProjectsCSV({ file });
      } catch (error: any) {
        // An import already in progress is signalled by a 403 from the backend.
        const errorResponse = await error?.response?.json?.().catch(() => null);
        const isImportInProgress = error?.response?.status === 403 || errorResponse?.statusCode === 403;
        if (isImportInProgress) {
          feedback.error("Un import est déjà en cours, veuillez attendre avant de relancer", "small");
        } else {
          feedback.error("Erreur Interne", "small");
        }
        return null;
      }
    },

    async fetchProjectIndividual(id: string) {
      // Empty training plan individual since it's the same modal
      const sessionStore = useSessionsStore();
      const feedback = useFeedback();
      sessionStore.trainingPlanSessionIndividual = null;

      this.trainingPlanProjectIndividual = null;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPlanApi);
        this.trainingPlanProjectIndividual = await apiInstance.findOneTrainingPlan({ trainingPlanId: id });
      } catch {
        feedback.error(`Erreur lors du chargement du projet`, "small");
      }
    },

    async removeProjectUsers(
      projectId: string,
      userIds: string[],
      requestAction?: RemoveProjectUsersDtoRequestActionEnum | null,
      requestComment?: string | null,
    ) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);
        await apiInstance.removeProjectUsers({
          projectId,
          removeProjectUsersDto: {
            userIds,
            rollback: false,
            ...(requestAction && { requestAction }),
            ...(requestComment && { requestComment }),
          },
        });
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async removeProject(projectId: string, requestAction?: RemoveBookingDtoRequestActionEnum, requestComment?: string) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ProjectsApi);
      try {
        await apiInstance.removeProject({
          projectId,
          removeProjectDto: {
            ...(requestAction && { requestAction }),
            ...(requestComment && { requestComment }),
          },
        });
        feedback.success(`Vos modifications ont été sauvegardées`, "small");
      } catch (error) {
        console.error(error);
        return false;
      }
    },
  },
});
